import { useTheme } from "@mui-wrapper";
import { IconButton, Tooltip } from "@flat-ui";
import {
  Add,
  Check,
  Close,
  Remove,
  SvgIconComponent,
} from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";

/** The palette entries used to tell an available feature from an unavailable one */
type AvailabilityPalette = "success" | "error";

interface AvailabilityDisplay {
  /** Icon shown for the current availability */
  Icon: SvgIconComponent;

  /** Palette color of the icon shown for the current availability */
  palette: AvailabilityPalette;

  /** Icon shown while hovering, previewing the action that a click would trigger */
  ToggleIcon: SvgIconComponent;

  /** Palette color of the toggle icon */
  togglePalette: AvailabilityPalette;

  /** Label of the action that a click would trigger */
  toggleLabel: string;
}

const AVAILABILITY_DISPLAY: Record<
  "available" | "unavailable",
  AvailabilityDisplay
> = {
  available: {
    Icon: Check,
    palette: "success",
    ToggleIcon: Remove,
    togglePalette: "error",
    toggleLabel: "Remove this feature from the bundle",
  },
  unavailable: {
    Icon: Close,
    palette: "error",
    ToggleIcon: Add,
    togglePalette: "success",
    toggleLabel: "Add this feature to the bundle",
  },
};

function getAvailabilityDisplay(isAvailable: boolean): AvailabilityDisplay {
  return AVAILABILITY_DISPLAY[isAvailable ? "available" : "unavailable"];
}

interface AvailabilityIconProps {
  /** Whether the feature is part of the product or bundle */
  isAvailable: boolean;
}

/** Shows whether a feature is part of a product: a check when it is, a cross otherwise */
export function AvailabilityIcon({
  isAvailable,
}: AvailabilityIconProps): React.JSX.Element {
  const { Icon, palette } = getAvailabilityDisplay(isAvailable);

  return <Icon sx={{ color: `${palette}.main` }} />;
}

interface EditableAvailabilityCellProps extends AvailabilityIconProps {
  /** Called when the user asks to add the feature to the bundle or remove it from it */
  onToggle(): void;
}

let stopHoveringLastCell: (() => void) | undefined;

/**
 * Hands the hover over to a cell and takes it away from the one that had it. Browsers skip the
 * leave event when a cell moves out from under a still pointer, as the data grid does while it
 * scrolls, so cells clear each other instead of each relying on being told that it was left.
 *
 * @param stopHovering clears the hover of the cell claiming it
 */
function claimHover(stopHovering: () => void): void {
  if (stopHoveringLastCell !== stopHovering) {
    stopHoveringLastCell?.();
    stopHoveringLastCell = stopHovering;
  }
}

/**
 * Availability icon that turns into the opposite action while hovered, so a check becomes a red
 * minus and a cross becomes a green plus, and triggers that action when clicked.
 */
export function EditableAvailabilityCell({
  isAvailable,
  onToggle,
}: EditableAvailabilityCellProps): React.JSX.Element {
  const theme = useTheme();
  const [isPreviewingToggle, setIsPreviewingToggle] = useState<boolean>(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);
  const { Icon, palette, ToggleIcon, togglePalette, toggleLabel } =
    getAvailabilityDisplay(isAvailable);
  const ShownIcon = isPreviewingToggle ? ToggleIcon : Icon;
  const isHovered = isPreviewingToggle || isTooltipOpen;

  const stopHovering = useCallback(() => {
    setIsPreviewingToggle(false);
    setIsTooltipOpen(false);
  }, []);

  // Nothing is hovered after a scroll, so no other cell will claim the hover away from this one
  useEffect(() => {
    if (!isHovered) {
      return;
    }

    // Capturing, because the scroll events of the grid's scroller do not bubble
    window.addEventListener("scroll", stopHovering, true);
    return () => window.removeEventListener("scroll", stopHovering, true);
  }, [isHovered, stopHovering]);

  return (
    <Tooltip
      title={toggleLabel}
      open={isTooltipOpen}
      onOpen={() => {
        claimHover(stopHovering);
        setIsTooltipOpen(true);
      }}
      onClose={stopHovering}
    >
      {/* The color comes from these props because the button overrides the icon's own sx */}
      <IconButton
        aria-label={toggleLabel}
        color={theme.palette[palette].main}
        hoverColor={theme.palette[togglePalette].main}
        onMouseEnter={() => {
          claimHover(stopHovering);
          setIsPreviewingToggle(true);
        }}
        onMouseLeave={stopHovering}
        onClick={onToggle}
      >
        <ShownIcon />
      </IconButton>
    </Tooltip>
  );
}
